import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import {
  GetOrderProductionQualityChecklistAPI,
  UpdateOrderProductionQualityChecklistAPI,
} from "../../Redux/orders";
import {
  Spin,
  Card,
  Typography,
  Empty,
  Table,
  Tag,
  Modal,
  Button,
  message,
  Menu,
  Dropdown,
  Tooltip,
} from "antd";
import {
  FileOutlined,
  EyeOutlined,
  EditOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { getFullAwsUrl, momentFullTime } from "../../../helpers/utils";
import FillQualityChecklistModal from "../Manufacturing/Components/FillQualityChecklistModal";
import { svg } from "../../../Assets/svg";
import CommonFilePreviewModal from "../../Components/CommonFilePreviewModal";
import FullScreenLoader from "../../Components/FullScreenLoader";

const { Text } = Typography;

const QualityChecklistData = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);

  const { updateOrderProductionQualityChecklistLoading } = useAppSelector(
    (state: RootState) => state?.loading,
  );
  const [viewMode, setViewMode] = useState<"LIST" | "COMPARE">("LIST");
  const [compareData, setCompareData] = useState<any>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentEditChecklist, setCurrentEditChecklist] = useState<any>(null);
  const [initialAnswers, setInitialAnswers] = useState<any>({});
  const [checklistType, setChecklistType] = useState<any>(false);

  const [previewFiles, setPreviewFiles] = useState<any>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [refreshData,setRefreshData] = useState(1)
  const fetchData = () => {
    if (id) {
      setLoading(true);
      dispatch(
        GetOrderProductionQualityChecklistAPI({ order_id: id }, (resData) => {
          setData(resData || []);
          setLoading(false);
        }),
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, dispatch,refreshData]);

  if (loading) {
    return (
    <FullScreenLoader/>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="shadow-sm border-0 mt-3">
        <Empty description="No quality checklist data found for this order" />
      </Card>
    );
  }

  const masterTableData: any[] = [];
  data?.forEach((item: any) => {
    const rawChecklists = item?.quality_checklists || [];
    const finalChecklists = rawChecklists.filter(
      (c: any) => c.full_complete === true || c.half_complete === false
    );
    const checklistsToUse =
      finalChecklists.length > 0 ? finalChecklists : rawChecklists;

    checklistsToUse.forEach((checklistGroup: any, idx: number) => {
      masterTableData.push({
        key: `${item.product_name}-${idx}`,
        product_name: item.product_name,
        checklist_title: checklistGroup?.checklist_id?.title || "N/A",
        department_name: checklistGroup?.department?.name || "N/A",
        half_complete: checklistGroup?.half_complete,
        full_complete: checklistGroup?.full_complete,
        questions_data: checklistGroup?.questions_data,
        original_questions:
          checklistGroup?.checklist_id?.questions ||
          (checklistGroup?.questions_data || []).map((q: any) => ({
            ...q,
            _id: q.question_id || q._id,
          })),
        group_id: checklistGroup?.group_id,
        split_id: checklistGroup?.split_id,
        planning_id: checklistGroup?.planning_id,
        submitted_by: checklistGroup?.submitted_by,
        submitted_on: checklistGroup?.submitted_on,
        updated_by: checklistGroup?.updated_by,
        is_answered: checklistGroup?.is_answered,
        selected_user: checklistGroup?.selected_user,
        quantity: checklistGroup?.quantity,
      });
    });
  });

  // Calculate rowSpan for Product Name, Department Name, Checklist Title
  for (let i = 0; i < masterTableData.length; i++) {
    const current = masterTableData[i];

    // Calculate productNameRowSpan
    if (current.productNameRowSpan === undefined) {
      let span = 1;
      for (let j = i + 1; j < masterTableData.length; j++) {
        if (masterTableData[j].product_name === current.product_name) {
          span++;
        } else {
          break;
        }
      }
      current.productNameRowSpan = span;
      for (let k = 1; k < span; k++) {
        masterTableData[i + k].productNameRowSpan = 0;
      }
    }

    // Calculate departmentNameRowSpan
    if (current.departmentNameRowSpan === undefined) {
      let span = 1;
      for (let j = i + 1; j < masterTableData.length; j++) {
        if (
          masterTableData[j].product_name === current.product_name &&
          masterTableData[j].department_name === current.department_name
        ) {
          span++;
        } else {
          break;
        }
      }
      current.departmentNameRowSpan = span;
      for (let k = 1; k < span; k++) {
        masterTableData[i + k].departmentNameRowSpan = 0;
      }
    }

    // Calculate checklistTitleRowSpan
    if (current.checklistTitleRowSpan === undefined) {
      let span = 1;
      for (let j = i + 1; j < masterTableData.length; j++) {
        if (
          masterTableData[j].product_name === current.product_name &&
          masterTableData[j].department_name === current.department_name &&
          masterTableData[j].checklist_title === current.checklist_title
        ) {
          span++;
        } else {
          break;
        }
      }
      current.checklistTitleRowSpan = span;
      for (let k = 1; k < span; k++) {
        masterTableData[i + k].checklistTitleRowSpan = 0;
      }
    }
  }

  const handleEditAnswers = (record: any) => {
    if (!record.questions_data) {
      message.error("Cannot edit checklist: questions data is missing.");
      return;
    }

    // Format answers from questions_data to { [questionId]: { ans_type, answer, conditional_answer, photo } }
    const formattedInitialAnswers: any = {};
    if (record.questions_data && Array.isArray(record.questions_data)) {
      record.questions_data.forEach((q: any) => {
        formattedInitialAnswers[q.question_id] = {
          ans_type: q.ans_type,
          answer: q.answer,
          conditional_answer: q.conditional_answer,
          photo: q.photo,
          photo_required: q.photo_required,
        };
      });
    }

    setInitialAnswers(formattedInitialAnswers);
    setChecklistType(record?.half_complete);
    setCurrentEditChecklist(record);
    setEditModalVisible(true);
  };

  const handleSaveEdit = (answers: any) => {
    if (!currentEditChecklist) return;

    const formattedAnswers = currentEditChecklist.questions_data.map(
      (q: any) => {
        const qId = q.question_id || q._id;
        const entry = answers[qId] || {};
        const isQAnswered =
          entry.answer !== null &&
          entry.answer !== undefined &&
          entry.answer !== "";
        return {
          question_id: qId,
          question_text: q.question_text || "",
          ans_type: q.ans_type,
          answer: entry.answer,
          conditional_answer: entry.conditional_answer,
          photo: entry.photo || [],
          photo_required: q.photo_required,
          if_yes_action: q.if_yes_action || null,
          if_no_action: q.if_no_action || null,
          is_answered:
            entry.is_answered !== undefined ? entry.is_answered : isQAnswered,
        };
      },
    );

    const payload = {
      planning_id: currentEditChecklist.planning_id,
      split_id: currentEditChecklist.split_id,
      group_id: currentEditChecklist.group_id,
      questions_data: formattedAnswers,
    };

    dispatch(
      UpdateOrderProductionQualityChecklistAPI(payload, () => {
        message.success("Checklist updated successfully!");
        setEditModalVisible(false);
        fetchData();
      }),
    );
  };

  const handleViewAnswers = (record: any) => {
    setCompareData({
      product_name: record.product_name,
      department_name: record.department_name,
      checklist_title: record.checklist_title,
      questions: record.questions_data || [],
      quantity: record.quantity,
    });
    setViewMode("COMPARE");
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        <Menu.Item key="1" onClick={() => handleViewAnswers(data)}>
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            View
          </div>
        </Menu.Item>

        {data?.submitted_by ? (
          <Menu.Item key="2" onClick={() => handleEditAnswers(data)}>
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Modify
            </div>
          </Menu.Item>
        ) : (
          ""
        )}
      </Menu>
    );
  };
  const masterColumns = [
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      width: "20rem",
      onCell: (record: any) => ({
        rowSpan: record.productNameRowSpan,
        style: { verticalAlign: "top" },
      }),
      render: (text: string) => {
        return <Text strong>{text}</Text>;
      },
    },
    {
      title: "Department Name",
      dataIndex: "department_name",
      key: "department_name",
      onCell: (record: any) => ({
        rowSpan: record.departmentNameRowSpan,
        style: { verticalAlign: "top" },
      }),
      render: (text: string) => text,
    },
    {
      title: "Checklist Title",
      dataIndex: "checklist_title",
      key: "checklist_title",
      onCell: (record: any) => ({
        rowSpan: record.checklistTitleRowSpan,
        style: { verticalAlign: "top" },
      }),
      width:300,
      render: (text: string) => text,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      render: (quantity: any) => {
        return (
          <div>
            {quantity !== null && quantity !== undefined ? quantity : "-"}
          </div>
        );
      },
    },
    {
      title: "Submited By",
      dataIndex: "selected_user",
      key: "selected_user",
      render: (selected_user: any,record:any) => {
        const submitedBy = `${record?.submitted_by?.first_name || ""} ${record?.submitted_by?.last_name || ""}`;
        return submitedBy?.trim() ?(
          <div>
            <Tooltip title={submitedBy?submitedBy:""}>

            {selected_user?.first_name
              ? (selected_user?.first_name || "") +
                " " +
                (selected_user?.last_name || "")
              : "-"}
              </Tooltip>
          </div>
        ):"-";
      },
    },
    {
      title: "Submited On",
      dataIndex: "submitted_on",
      key: "submitted_on",
      render: (submitted_on: any) => {
        console.log(submitted_on, "submitted_onsubmitted_on");
        return <div>{submitted_on ? momentFullTime(submitted_on) : "-"}</div>;
      },
    },
    {
      title: "Updated By",
      dataIndex: "updated_by",
      key: "updated_by",
      render: (updated_by: any) => (
        <div>
          {updated_by?.first_name
            ? (updated_by?.first_name || "") +
              " " +
              (updated_by?.last_name || "")
            : "-"}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: any) => (
        <div
          className="d-flex justify-content-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            overlay={() => MoreOptionsMenu(record)}
            trigger={["click"]}
            placement="bottomRight"
            className="cursor-pointer"
            overlayClassName="moreOptionDropdown"
          >
            <span className="svg-28 svg-outline-primary">
              {svg?.tableMoreOption}
            </span>
          </Dropdown>
        </div>
      ),
    },
  ];

  const renderCompareView = () => {
    if (!compareData) return null;

    const compareDataSource = compareData.questions || [];

    const renderAnswer = (answer: any) => {
      if (Array.isArray(answer)) {
        return answer && answer?.length > 0 ? (
          <div className="d-flex flex-column gap-2">
            {answer?.map((file: string, index: number) => (
              <span
                key={index}
                onClick={() => {
                  setPreviewModalOpen(true);
                  setPreviewFiles(file);
                }}
                className="d-flex align-items-center gap-1 cursor-pointer"
              >
                <FileOutlined /> View File
              </span>
            ))}
          </div>
        ) : (
          "-"
        );
      }
      if (typeof answer === "boolean") {
        return answer ? "Yes" : "No";
      }
      return answer || "-";
    };

    const renderCombinedAnswer = (
      answer: any,
      conditional: any,
      photo: any,
    ) => {
      return (
        <div className="d-flex flex-column gap-2">
          <div>{renderAnswer(answer)}</div>

          {conditional &&
            (Array.isArray(conditional) ? conditional.length > 0 : true) && (
              <div className="mt-2 pt-2 border-top">
                <span className="text-muted fs-8 d-block mb-1">
                  Conditional Answer:
                </span>
                {renderAnswer(conditional)}
              </div>
            )}

          {photo && photo.length > 0 && (
            <div className="mt-2 pt-2 border-top">
              <span className="text-muted fs-8 d-block mb-1">
                Required Photo:
              </span>
              {renderAnswer(photo)}
            </div>
          )}
        </div>
      );
    };

    const compareColumns: any = [
      {
        title: "Question",
        dataIndex: "question_text",
        key: "question_text",
        render: (text: string) => <Text strong>{text || "N/A"}</Text>,
        width: 300,
      },
      {
        title: `Final Checklist ${
          compareData?.quantity
            ? `(QTY - ${compareData?.quantity})`
            : ""
        }`,
        key: "answer",
        render: (_: any, record: any) =>
          renderCombinedAnswer(
            record.answer,
            record.conditional_answer,
            record.photo,
          ),
        width: 500,
      },
    ];

    return (
      <div className="mt-3">
        <div className="mb-3 d-flex align-items-center gap-3">
          <span
            onClick={() => setViewMode("LIST")}
            className="svg-24 cursor-pointer"
          >
            {svg?.backArrow}
          </span>
          <h5 className="mb-0 fw-bold">
            {compareData?.product_name} - {compareData?.department_name} (
            {compareData?.checklist_title})
          </h5>
        </div>
        <div className="card page-table-device border-0 shadow rounded-4 flex-fill overflow-y-auto">
          <div className="card-body overflow-y-auto">
            <Table
              dataSource={compareDataSource}
              columns={compareColumns}
              pagination={false}
              rowKey={(record: any, i: any) => `${record?.question_text}-${i}`}
              scroll={{ x: "max-content" }}
                className="custom-table ordersTable cursor-pointer"
              bordered
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {viewMode === "LIST" && (
        <div className="mt-3">
          <div className="d-flex align-items-center mb-3 d-flex align-items-center justify-content-between">
            <h4 className="fw-bold ">Quality Checklist Data</h4>
            <div className="d-flex align-items-center justify-content-between gap-2 bg-white rounded border fs-6 fs-6-mob gap-2 order-sm-3 order-2">
              <Tooltip title="Refresh Quality Checklist Data">
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshData((prev:any)=>prev+1)}
                  className="btn btn-primary btn-36 d-flex align-items-center justify-content-center"
                />
              </Tooltip>
            </div>
          </div>
          <div className="card page-table-device border-0 shadow rounded-4 flex-fill overflow-y-auto">
            <div className="card-body overflow-y-auto">
              <Table
                dataSource={masterTableData}
                columns={masterColumns}
                pagination={false}
                scroll={{ x: "max-content" }}
                className="custom-table ordersTable cursor-pointer"
                onRow={(record) => ({
                  onClick: () => {
                    handleViewAnswers(record);
                  },
                })}
                bordered
              />
            </div>
          </div>
        </div>
      )}
      {viewMode === "COMPARE" && renderCompareView()}

      {editModalVisible && (
        <FillQualityChecklistModal
          visible={editModalVisible}
          checklistQuestions={(currentEditChecklist?.questions_data || []).map(
            (q: any) => ({
              ...q,
              _id: q.question_id || q._id,
            }),
          )}
          setIsModalVisible={setEditModalVisible}
          onOk={handleSaveEdit}
          initialAnswers={initialAnswers}
          isHalfComplete={checklistType}
          loading={updateOrderProductionQualityChecklistLoading}
        />
      )}
      {previewModalOpen && (
        <CommonFilePreviewModal
          visible={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewFiles([]);
          }}
          file={previewFiles}
        />
      )}
    </>
  );
};

export default QualityChecklistData;
